import { BillSearchView, SortByParam } from '@enview/interface/types/BillSearch';
import { Person } from '@enview/interface/types/Person';
import { ReactElement, RefObject, useEffect, useMemo, useState } from 'react';
import TopBarProgress from 'react-topbar-progress-indicator';
import {
  ArrayParam,
  DateParam,
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';
import { BillAPI, JurisdictionAPI } from '../../api';
import {
  buildParamsFromQuery,
  getSessionOptions,
} from '../../helpers/BillSearchHelper';
import { SortBy, SortOrder } from '../../models/SortOrder';
import BillList from '../Shared/Lists/BillList';
import SortAndFilterHeader from '../Shared/Lists/SortAndFilterHeader';
import Paginator from '../Shared/PageElements/Paginator';
import BillTable from '../Shared/Tables/BillTable';
import { getJurisdictions } from './PersonHelper';
import { useSelector } from 'react-redux';
import { getTeamMode } from '../../dux';
import { skipToken } from '@reduxjs/toolkit/dist/query';

const SPONSORED_BILLS_SORT_OPTIONS = [
  new SortOrder(SortBy.RECENT, false),
  new SortOrder(SortBy.RECENT, true),
  new SortOrder(SortBy.ALPHANUMERICAL, false),
  new SortOrder(SortBy.ALPHANUMERICAL, true),
];

const NO_BILL_MESSAGE = 'This person has not sponsored any bills yet.';

type BillsSponsoredProps = {
  person: Person;
  scrollRef: RefObject<HTMLDivElement>;
  view?: BillSearchView;
};
/*
  This component presents two different views, depending on a user setting. The
  "card view" is rendered as <BillList>, the "table view" is rendered as <BillTable>.
  Both views utilize the browser's query string (via the useQueryParams() hook) as a
  piece of shared state. This means that sort & filter controls in either view work by
  calling setQuery() to modify the query string, which then causes a different parameters
  object to come out of buildParamsFromQuery and a new HTTP request to be fired.

  This component is part of a publicly-accessible URL and some legislators have sponsored
  many bills, so it's important that these components continue to use server-side paging/sorting
*/
const BillsSponsored = (props: BillsSponsoredProps): ReactElement => {
  const { person, scrollRef, view } = props;

  const activeTeamId = useSelector(getTeamMode);

  const [jurisdictions, setJurisdictions] = useState<string[]>([]);
  const { data: jurisdictionSessions } =
    JurisdictionAPI.endpoints.getSessions.useQuery(jurisdictions);
  const sessionOptions = getSessionOptions(jurisdictionSessions || [], true);
  const [query, setQuery] = useQueryParams({
    page: withDefault(NumberParam, 1),
    pageSize: withDefault(NumberParam, 20),
    order: withDefault(StringParam, SortByParam.ALPHANUMERICAL),
    jurisdictions: withDefault(ArrayParam, jurisdictions),
    status: ArrayParam,
    sessions: withDefault(ArrayParam, ['Current Sessions']),
    searchActionType: StringParam,
    searchActionFromDate: DateParam,
    searchActionToDate: DateParam,
    sponsors: ArrayParam,
  });
  const [searching, setSearching] = useState(false);

  const billSearchParams = useMemo(() => {
    // using jurisdictions[] object as a proxy for "have default query-string params"
    // been applied yet. We can skip an unneeded request if they have not
    if (query && query?.jurisdictions?.length > 0)
      return buildParamsFromQuery(query, activeTeamId, query.pageSize);
    return null;
  }, [query, activeTeamId]);
  const { data: sponsoredBills, isSuccess } =
    BillAPI.endpoints.getSponsoredBills.useQuery(
      billSearchParams
        ? {
            personId: person.id,
            searchParams: billSearchParams,
          }
        : skipToken,
    );

  const updateSessionSelection = (sessions: string[]): void => {
    setQuery({ sessions: sessions, page: 1 }, 'replaceIn');
  };

  const updateOrderSelection = (order: SortOrder): void => {
    setQuery({ page: 1, order: order.getQueryParam() }, 'replaceIn');
  };

  useEffect(() => {
    if (scrollRef.current && isSuccess) {
      scrollRef.current.scrollIntoView();
    }
  }, [isSuccess, scrollRef]);

  useEffect(() => {
    const jurisdictionsForPerson: string[] = getJurisdictions(person);
    setJurisdictions(jurisdictionsForPerson);
    setQuery({ jurisdictions: jurisdictionsForPerson });
    setQuery({ page: query.page });
  }, [person]);

  const progressBar = (
    <div id="search-progress-bar">
      <TopBarProgress />
    </div>
  );

  // Pagination for List view
  const pageSize = 20;
  const pages = {
    page: query.page,
    total: sponsoredBills?.count ?? 0,
    pageSize,
  };
  const currentPageOfBills = sponsoredBills?.data ?? [];

  // fighting the type system to let me pass the results of query.sessions to a
  // prop expecting an array of strings
  const selectedSessions: string[] =
    query?.sessions?.map((s) => (typeof s === 'string' ? s : '')) ?? [];

  const renderBillList = (): ReactElement => {
    return (
      <div className="list">
        <SortAndFilterHeader
          filterLabel="Session"
          filterOptions={sessionOptions}
          selectedFilters={selectedSessions}
          setSelectedFilters={updateSessionSelection}
          setSortOrder={updateOrderSelection}
          showViewDropdown
          sortOptions={SPONSORED_BILLS_SORT_OPTIONS}
          sortOrder={SortOrder.getBillSortOrderFromParam(query.order)}
          totalBills={pages.total}
        />
        <BillList bills={currentPageOfBills} noBillsMessage={NO_BILL_MESSAGE} />
        <Paginator onChange={(page) => setQuery({ page })} pages={pages} />
      </div>
    );
  };

  const renderBillTable: () => ReactElement = () => {
    const results = sponsoredBills ?? { data: [], count: 0 };
    const sessionFilterProperties = {
      show: true,
      filterLabel: 'Session',
      filterOptions: [...sessionOptions],
    };
    return (
      <BillTable
        filterProperties={sessionFilterProperties}
        query={query}
        results={results}
        searching={searching}
        setQuery={setQuery}
        setSearching={setSearching}
        showFilterProperties={sessionFilterProperties.show}
        useServerSidePagination={true}
      />
    );
  };

  return (
    <>
      {!isSuccess && progressBar}
      <div className="bill-search-container sponsored-bills">
        <div className="results-pane list">
          {view === BillSearchView.Card ? renderBillList() : renderBillTable()}
        </div>
      </div>
    </>
  );
};

export default BillsSponsored;
